import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ApiAmountWithCurrency } from '../api';
import { useAuth } from '../hooks/useAuth';
import { formatAmountInCurrency } from '../utils';
import { IPointLoyalty } from '../utils/types';
import AmountInput from './AmountInput';

type OrderListItemProps = {
  order: number;
  firstName: string;
  lastName: string;
  tourName: string;
  tourStartName: string;
  pnr: string;
  orderNumber: string;
  bedTypeName: string;
  selected?: boolean;
  onSelection?: (v: boolean) => void;
  readonly?: boolean;
  balance: ApiAmountWithCurrency;
  total: ApiAmountWithCurrency;
  received: ApiAmountWithCurrency;
  toPay: ApiAmountWithCurrency;
  loyalty?: {
    points: number;
    fiat: number;
  };
  onPayWithLoyaltyClick?: () => void;
  onChangeAmount: (val: number) => void;
  onRemoveLoyaltyPoints?: () => void;
};

export function OrderListItem(props: OrderListItemProps) {
  const formattedDate = (date?: string) => {
    const dateOptions: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
    };

    if (date) return new Date(date).toLocaleDateString('tr-TR', dateOptions);
    return '-';
  };

  return (
    <tr>
      <td>
        {props.onSelection ? (
          <FormControlLabel
            control={
              <Checkbox
                color="secondary"
                disabled={props.readonly}
                value={props.orderNumber}
                key={props.orderNumber}
                checked={props.selected}
                onChange={() =>
                  props.onSelection &&
                  props.onSelection(props.selected ? false : true)
                }
              />
            }
            label={
              props.firstName || props.lastName
                ? `${props.firstName} ${props.lastName}`
                : '-'
            }
            sx={{
              display: 'flex',
              alignItems: 'center',
              textAlign: 'left',
            }}
          />
        ) : (
          <Typography sx={{ color: 'inherit' }}>
            {props.firstName || props.lastName
              ? `${props.firstName} ${props.lastName}`
              : '-'}
          </Typography>
        )}
      </td>
      <td>
        <PayAmountColumn
          balance={props.balance}
          total={props.total}
          toPay={props.toPay}
          onPayWithLoyaltyClick={props.onPayWithLoyaltyClick}
          onRemoveLoyaltyPoints={props.onRemoveLoyaltyPoints}
          onChangeAmount={props.onChangeAmount}
          loyalty={props.loyalty}
          readonly={props.readonly}
          selected={props.selected}
        />
      </td>
      <td>
        <Typography sx={{ color: 'inherit' }}>
          {formatAmountInCurrency(
            props.total.amount || 0,
            props.total?.currency?.code || 'TRY',
          )}
        </Typography>
      </td>
      <td>
        <Typography sx={{ color: 'inherit' }}>
          {formatAmountInCurrency(
            props.received.amount,
            props.received?.currency?.code || 'TRY',
          )}
        </Typography>
      </td>
      <td>
        <Typography sx={{ color: 'inherit' }}>
          {props.tourName || '-'}
        </Typography>
      </td>
      <td>
        <Typography sx={{ color: 'inherit' }}>
          {formattedDate(props.tourStartName) || '-'}
        </Typography>
      </td>
      <td>
        <Typography sx={{ color: 'inherit' }}>
          {props.bedTypeName || ''}
        </Typography>
      </td>
      <td>
        <Typography sx={{ color: 'inherit' }}>
          {props.orderNumber || ''}
        </Typography>
      </td>
      <td>
        <Typography sx={{ color: 'inherit' }}>{props.pnr || '-'}</Typography>
      </td>
    </tr>
  );
}

function PayAmountColumn(props: {
  readonly?: boolean;
  selected?: boolean;
  balance: ApiAmountWithCurrency;
  total: ApiAmountWithCurrency;
  toPay: ApiAmountWithCurrency;
  onPayWithLoyaltyClick?: () => void;
  onRemoveLoyaltyPoints?: () => void;
  onChangeAmount: (val: number) => void;
  loyalty?: IPointLoyalty;
}) {
  const { token } = useAuth();
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Typography sx={{ fontSize: '14px' }} variant="inherit">
        (
        {formatAmountInCurrency(
          props.balance.amount || 0,
          props.balance?.currency?.code || 'EUR',
        )}
        )
      </Typography>
      <AmountInput
        disabled={props.readonly}
        value={props.toPay.amount}
        onChange={props.onChangeAmount}
        currencySymbol={
          formatAmountInCurrency(
            props.toPay.amount,
            props.toPay.currency?.code || 'TRY',
          )[0]
        }
      />
      {props?.selected && token && (
        <>
          {(props.loyalty?.fiat || 0) > 0 &&
            props.loyalty?.currencyExchangeRate && (
              <Typography variant="caption">
                {formatAmountInCurrency(
                  props.loyalty.fiat / props.loyalty.currencyExchangeRate || 0,
                  'TRY',
                )}{' '}
                {t('orderListItem.usedPoint')}
              </Typography>
            )}
          {!props.readonly && (
            <Button
              color={props.loyalty?.points ? 'error' : 'primary'}
              size="small"
              variant="contained"
              sx={{
                mt: 1,
                fontSize: 14,
                width: 'calc(100%)',
              }}
              onClick={
                props.loyalty?.points
                  ? props.onRemoveLoyaltyPoints
                  : props.onPayWithLoyaltyClick
              }
            >
              {props.loyalty?.points
                ? t('orderListItem.cancelPaymentWithPoint')
                : t('orderListItem.paymentWithPoints')}
            </Button>
          )}
        </>
      )}
    </Box>
  );
}
